import dayjs from 'dayjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Box, Divider, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import Modal from '@Components/Modal';

import { getNonAccountUnbilledList } from '@Reducers/Invoice';
import { ShipmentObj } from '@Reducers/Invoice/Type';
import { getInvoiceSettings } from '@Reducers/OrganizationSetup';
import { sendNonAccountDataTo365 } from '@Reducers/d365';
import { AppDispatch, RootStateType } from '@Store';

import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import {
  NonAccountFilterType,
  NonAccountInvConfig,
  calculateNonAccountSelectedPricingSum,
  getIsWyngsMy,
  initialNonAccountInvConfig,
  isShipmentSelectable,
  validateNonAccountInvoiceConfig,
} from '../utils/nonAccountInvoiceUtils';
import NonAccountConfigSection from './NonAccountConfigSection';
import NonAccountShipmentSection from './NonAccountShipmentSection';

export interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  systemList: { display: string; value: string }[];
  isSubmitting: boolean;
  onSubmitInvoice: (config: NonAccountInvConfig, selectedShipments: ShipmentObj[], onSuccess?: () => void) => void;
  initialFilterSelectValue: string;
  initialFilterText: string;
  initialDateRange: { startDate: Date; endDate: Date };
  initialSystem: string[];
  initialShipmentType: string[];
  onRestorePageFilters: () => void;
}

const CreateInvoiceModal = React.memo(function CreateInvoiceModal(props: CreateInvoiceModalProps) {
  const {
    open,
    onClose,
    systemList,
    isSubmitting,
    onSubmitInvoice,
    initialFilterSelectValue,
    initialFilterText,
    initialDateRange: pageDateRange,
    initialSystem,
    initialShipmentType,
    onRestorePageFilters,
  } = props;

  const theme = useTheme();
  const locale = useLocale();
  const dispatch = useDispatch<AppDispatch>();
  const isWyngsMy = useMemo(() => getIsWyngsMy(), []);

  const {
    nonAccountData: { list = [], currentPage = 1, perPage = 10, totalCount = 0 },
    loading: listLoading,
  } = useSelector((state: RootStateType) => state.Invoice);

  const {
    invoiceSetting,
    loading: orgLoading,
    organizationPublicData,
    organizationData,
  } = useSelector((state: RootStateType) => state.orgSetup);
  const { loading: loadingD365 } = useSelector((state: RootStateType) => state.D365);
  const { name: orgName = '' } = organizationPublicData || {};
  const orgCurrency = organizationData?.currency?.name || '';

  const { sacCode = [], taxInformation = [] } = invoiceSetting || {};
  const sacCodeOptions = useMemo(() => sacCode.map(s => ({ display: s.display, value: s.key })), [sacCode]);
  const taxInformationOptions = useMemo(
    () => taxInformation.map(s => ({ display: s.display, value: s.value, key: s.key })),
    [taxInformation],
  );

  const [modalFilterSelectValue, setModalFilterSelectValue] = useState(initialFilterSelectValue);
  const [modalFilterText, setModalFilterText] = useState(initialFilterText);
  const [modalDateRange, setModalDateRange] = useState(pageDateRange);
  const [modalSystem, setModalSystem] = useState<string[]>(initialSystem);
  const [modalShipmentType, setModalShipmentType] = useState<string[]>(initialShipmentType);
  const [hasModifiedModalFilters, setHasModifiedModalFilters] = useState(false);

  const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>([]);
  const [selectedShipmentsMap, setSelectedShipmentsMap] = useState<Record<string, ShipmentObj>>({});
  const [expandedShipments, setExpandedShipments] = useState<Record<string, boolean>>({});
  const [modalConfig, setModalConfig] = useState<NonAccountInvConfig>(initialNonAccountInvConfig);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setModalFilterSelectValue(initialFilterSelectValue);
      setModalFilterText(initialFilterText);
      setModalDateRange(pageDateRange);
      setModalSystem(initialSystem);
      setModalShipmentType(initialShipmentType);
      setHasModifiedModalFilters(false);
      setSelectedShipmentIds([]);
      setSelectedShipmentsMap({});
      setExpandedShipments({});
      setModalConfig(initialNonAccountInvConfig);
    }
    wasOpenRef.current = open;
  }, [open, initialFilterSelectValue, initialFilterText, pageDateRange, initialSystem, initialShipmentType]);

  useEffect(() => {
    if (open && !invoiceSetting && !orgLoading) {
      dispatch(getInvoiceSettings());
    }
  }, [open, invoiceSetting, orgLoading, dispatch]);

  const fetchModalData = useCallback(
    (customOverrides: Partial<NonAccountFilterType> = {}) => {
      const fromDate = moment(customOverrides.fromDate || modalDateRange.startDate).format('DD/MM/YYYY');
      const toDate = moment(customOverrides.toDate || modalDateRange.endDate).format('DD/MM/YYYY');
      const sys = customOverrides.systems !== undefined ? customOverrides.systems : modalSystem;
      const type = customOverrides.type !== undefined ? customOverrides.type : modalShipmentType;
      const searchKey = customOverrides.searchKey !== undefined ? customOverrides.searchKey : modalFilterSelectValue;
      const searchValue = customOverrides.searchValue !== undefined ? customOverrides.searchValue : modalFilterText;

      const filter: NonAccountFilterType = {
        currentPage: customOverrides.currentPage || 1,
        perPage: customOverrides.perPage || perPage || 10,
        fromDate,
        toDate,
        systems: sys,
        type,
      };

      if (searchValue) {
        filter.searchKey = searchKey;
        filter.searchValue = searchValue;
      }

      dispatch(getNonAccountUnbilledList(filter));
    },
    [modalDateRange, modalSystem, modalShipmentType, modalFilterSelectValue, modalFilterText, perPage, dispatch],
  );

  const handleCloseModalClean = useCallback(() => {
    if (isSubmitting) return;
    if (hasModifiedModalFilters) {
      onRestorePageFilters();
    }
    onClose();
  }, [isSubmitting, hasModifiedModalFilters, onRestorePageFilters, onClose]);

  const onModalSearchSelectChange = useCallback((value: string) => {
    setModalFilterSelectValue(value);
  }, []);

  const onModalSearchTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setModalFilterText(e.target.value);
  }, []);

  const onModalSearchTextSubmit = useCallback(() => {
    setHasModifiedModalFilters(true);
    fetchModalData({ currentPage: 1 });
  }, [fetchModalData]);

  const onModalTextClear = useCallback(() => {
    setModalFilterText('');
    setHasModifiedModalFilters(true);
    fetchModalData({ currentPage: 1, searchValue: '' });
  }, [fetchModalData]);

  const onModalDateChange = useCallback(
    (range: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      setModalDateRange(range);
      setHasModifiedModalFilters(true);
      const fromDate = moment(range.startDate).format('DD/MM/YYYY');
      const toDate = moment(range.endDate).format('DD/MM/YYYY');
      fetchModalData({ currentPage: 1, fromDate, toDate });
    },
    [fetchModalData],
  );

  const onModalSystemFilter = useCallback(
    (selected: string[]) => {
      setModalSystem(selected);
      setHasModifiedModalFilters(true);
      fetchModalData({ currentPage: 1, systems: selected });
    },
    [fetchModalData],
  );

  const onModalShipmentTypeFilter = useCallback(
    (types: string[]) => {
      setModalShipmentType(types);
      setHasModifiedModalFilters(true);
      fetchModalData({ currentPage: 1, type: types });
    },
    [fetchModalData],
  );

  const onModalClear = useCallback(() => {
    setModalFilterText('');
    setModalFilterSelectValue('shipment_no');
    setModalSystem([]);
    setModalShipmentType([]);
    setModalDateRange(pageDateRange);
    setHasModifiedModalFilters(true);
    const fromDate = moment(pageDateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(pageDateRange.endDate).format('DD/MM/YYYY');
    fetchModalData({
      currentPage: 1,
      searchValue: '',
      searchKey: 'shipment_no',
      systems: [],
      type: [],
      fromDate,
      toDate,
    });
  }, [pageDateRange, fetchModalData]);

  const onModalPageChange = useCallback(
    (_: unknown, newPage: number) => {
      fetchModalData({ currentPage: newPage + 1 });
    },
    [fetchModalData],
  );

  const onModalRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextPerPage = parseInt(event.target.value, 10);
      fetchModalData({ currentPage: 1, perPage: nextPerPage });
    },
    [fetchModalData],
  );

  const handleToggleSelectShipment = useCallback(
    (id: string, isChecked: boolean) => {
      if (isChecked) {
        const found = list.find(s => s._id === id);
        setSelectedShipmentIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        if (found) {
          setSelectedShipmentsMap(prev => ({ ...prev, [id]: found }));
        }
      } else {
        setSelectedShipmentIds(prev => prev.filter(i => i !== id));
        setSelectedShipmentsMap(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [list],
  );

  const handleSelectAllShipments = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        const selectable = list.filter(item => isShipmentSelectable(item, orgName));
        const newIds = Array.from(new Set([...selectedShipmentIds, ...selectable.map(s => s._id)]));
        const nextMap = { ...selectedShipmentsMap };
        selectable.forEach(s => {
          nextMap[s._id] = s;
        });
        setSelectedShipmentIds(newIds);
        setSelectedShipmentsMap(nextMap);
      } else {
        const pageIds = list.map(s => s._id);
        setSelectedShipmentIds(prev => prev.filter(id => !pageIds.includes(id)));
        setSelectedShipmentsMap(prev => {
          const next = { ...prev };
          pageIds.forEach(id => {
            delete next[id];
          });
          return next;
        });
      }
    },
    [list, orgName, selectedShipmentIds, selectedShipmentsMap],
  );

  const handleToggleExpandShipment = useCallback((id: string) => {
    setExpandedShipments(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleRemoveShipment = useCallback((id: string) => {
    setSelectedShipmentIds(prev => prev.filter(i => i !== id));
    setSelectedShipmentsMap(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const selectedShipmentsList = useMemo(() => {
    return selectedShipmentIds.map(id => selectedShipmentsMap[id]).filter(Boolean);
  }, [selectedShipmentIds, selectedShipmentsMap]);

  const totalSelectedAmount = useMemo(() => {
    return calculateNonAccountSelectedPricingSum(selectedShipmentsList);
  }, [selectedShipmentsList]);

  const handleModalSendTo365 = useCallback(() => {
    const shipmentNumbers = selectedShipmentsList.map(s => s.shipmentNumber);
    const payload = {
      shipmentIds: selectedShipmentIds,
      shipmentNumbers,
      dueDate: modalConfig.dueDate ? dayjs(modalConfig.dueDate).format('YYYY-MM-DD') : '',
      invoiceDate: modalConfig.invoiceDate ? dayjs(modalConfig.invoiceDate).format('YYYY-MM-DD') : '',
    };
    dispatch(sendNonAccountDataTo365(payload));
  }, [selectedShipmentsList, selectedShipmentIds, modalConfig, dispatch]);

  const handleModalSubmit = useCallback(() => {
    onSubmitInvoice(modalConfig, selectedShipmentsList, () => {
      onClose();
    });
  }, [onSubmitInvoice, modalConfig, selectedShipmentsList, onClose]);

  const missingFields = useMemo(() => {
    return validateNonAccountInvoiceConfig(modalConfig, selectedShipmentIds.length, isWyngsMy);
  }, [modalConfig, selectedShipmentIds.length, isWyngsMy]);

  const isConfigIncomplete = missingFields.length > 0;
  const isBusy = listLoading || loadingD365 || isSubmitting;
  const isCreateDisabled = isBusy || isConfigIncomplete;

  const disabledTooltipContent = useMemo(() => {
    if (isSubmitting) {
      return 'Creating invoice, please wait...';
    }
    if (listLoading || loadingD365) {
      return 'Loading data, please wait...';
    }
    if (missingFields.length > 0) {
      return (
        <Box sx={{ p: 0.5, maxWidth: 300 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5, color: 'inherit' }}>
            Cannot create invoice. Required field(s) missing:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', lineHeight: 1.5 }}>
            {missingFields.map(field => (
              <li key={field}>{field}</li>
            ))}
          </Box>
        </Box>
      );
    }
    return null;
  }, [isSubmitting, listLoading, loadingD365, missingFields]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModalClean}
      maxWidth="xl"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1480px',
          height: { xs: '96dvh', sm: '94dvh', md: '92dvh' },
          maxHeight: '95dvh',
          m: { xs: 1, sm: 2, md: 2.5 },
          borderRadius: '18px',
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
      dialogContainerSx={{
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2.5 },
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '18px',
        overflow: 'hidden',
      }}
    >
      {/* Top Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.2 }}
            >
              Create Non-Account Invoice
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
              Select unbilled shipments on the left and configure invoice parameters on the right
            </Typography>
          </Box>
        </Stack>

        {/* Live KPI Strip & Close Control */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 0.75,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>
                QUEUED SHIPMENTS
              </Typography>
              <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: 'text.primary' }}>
                {selectedShipmentIds.length}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
            <Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 600 }}>
                TOTAL AMOUNT
              </Typography>
              <Tooltip
                title={`Total Amount: ${orgCurrency} ${formatNumber(totalSelectedAmount, locale)}`}
                arrow
                placement="top"
              >
                <Typography sx={{ fontSize: '0.96rem', fontWeight: 800, color: 'primary.main' }}>
                  {orgCurrency} {formatNumber(totalSelectedAmount, locale)}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleCloseModalClean}
            sx={{
              color: 'text.secondary',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              p: 0.75,
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) },
            }}
            aria-label="Close modal"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Dual Workspace Body */}
      <Box sx={{ flex: 1, minHeight: 0, py: 1.5, overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%', minHeight: 0 }} spacing={0}>
          {/* Left Workspace: Shipments */}
          <Grid
            item
            xs={12}
            lg={7.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              pr: { lg: 1.75 },
              borderRight: { lg: `1px solid ${theme.palette.divider}` },
            }}
          >
            <NonAccountShipmentSection
              list={list}
              totalCount={totalCount}
              currentPage={currentPage}
              perPage={perPage}
              loading={listLoading}
              systemList={systemList}
              filterSelectValue={modalFilterSelectValue}
              filterText={modalFilterText}
              dateRange={modalDateRange}
              system={modalSystem}
              shipmentType={modalShipmentType}
              selectedShipmentIds={selectedShipmentIds}
              expandedShipments={expandedShipments}
              orgName={orgName}
              primaryCurrencySymbol={orgCurrency}
              onSearchSelectChange={onModalSearchSelectChange}
              onSearchTextChange={onModalSearchTextChange}
              onSearchTextSubmit={onModalSearchTextSubmit}
              onTextClear={onModalTextClear}
              onDateChange={onModalDateChange}
              onSystemFilter={onModalSystemFilter}
              onShipmentTypeFilter={onModalShipmentTypeFilter}
              onClearFilters={onModalClear}
              onPageChange={onModalPageChange}
              onRowsPerPageChange={onModalRowsPerPageChange}
              onSelectAllShipments={handleSelectAllShipments}
              onToggleSelectShipment={handleToggleSelectShipment}
              onToggleExpandShipment={handleToggleExpandShipment}
            />
          </Grid>

          {/* Right Workspace: Config & Preview */}
          <Grid
            item
            xs={12}
            lg={4.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              p: '0 !important',
              pl: { lg: '14px !important' },
            }}
          >
            <NonAccountConfigSection
              config={modalConfig}
              setConfig={setModalConfig}
              selectedShipments={selectedShipmentsList}
              totalSelectedAmount={totalSelectedAmount}
              taxInformationOptions={taxInformationOptions}
              sacCodeOptions={sacCodeOptions}
              orgName={orgName}
              isWyngsMy={isWyngsMy}
              loadingD365={loadingD365}
              primaryCurrencySymbol={orgCurrency}
              onSendTo365={handleModalSendTo365}
              onRemoveShipment={handleRemoveShipment}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Sticky Modal Footer Actions */}
      <Box
        sx={{
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.82rem' }}>
          {selectedShipmentIds.length > 0
            ? `${selectedShipmentIds.length} shipment(s) selected • Total: ${orgCurrency} ${formatNumber(
                totalSelectedAmount,
                locale,
              )}`
            : 'Select at least one shipment and set Invoice Date & Due Date to create an invoice.'}
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <SbButton
            variant="outlined"
            className="rounded-full"
            sx={{ minWidth: 115, height: '40px !important' }}
            onClick={handleCloseModalClean}
            disabled={isSubmitting}
          >
            Cancel
          </SbButton>
          <Tooltip
            title={disabledTooltipContent || ''}
            arrow
            enterDelay={150}
            placement="top"
            disableHoverListener={!isCreateDisabled}
          >
            <span style={{ display: 'inline-flex' }}>
              <SbButton
                variant="contained"
                className="rounded-full"
                sx={{ minWidth: 160, height: '40px !important' }}
                onClick={handleModalSubmit}
                disabled={isCreateDisabled}
                loading={isSubmitting}
              >
                Create Invoice
              </SbButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Modal>
  );
});

export default CreateInvoiceModal;
